import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Register() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
      setUser(data.user);
      toast.success("Account created — welcome to EasyStay!");
      nav("/verify", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center px-6 py-10">
      <div className="w-full rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EasyStay" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-display text-xl font-bold text-slate-900">Easy<span className="text-copper">Stay</span></span>
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">{t("auth.register.title")}</h1>
        <p className="mt-1 text-sm text-slate-600">{t("auth.register.subtitle")}</p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("auth.register.name")}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <UserIcon className="h-4 w-4 text-slate-400" />
              <input data-testid="reg-name" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Mwansa Kabwe" className="w-full bg-transparent py-3 text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("auth.register.email")}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <Mail className="h-4 w-4 text-slate-400" />
              <input data-testid="reg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="w-full bg-transparent py-3 text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("auth.register.password")}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <Lock className="h-4 w-4 text-slate-400" />
              <input data-testid="reg-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="w-full bg-transparent py-3 text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">{t("common.confirm")} {t("auth.register.password").toLowerCase()}</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 focus-within:border-copper focus-within:bg-white">
              <Lock className="h-4 w-4 text-slate-400" />
              <input data-testid="reg-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••" className="w-full bg-transparent py-3 text-sm outline-none" />
            </div>
          </div>
          <button data-testid="reg-submit" type="submit" disabled={loading} className="btn-primary w-full text-sm">
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("auth.register.submit")}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> {t("auth.register.orGoogle")} <span className="h-px flex-1 bg-slate-200" />
        </div>

        <button data-testid="reg-google" onClick={googleLogin} className="btn-outline w-full text-sm">
          {t("auth.register.orGoogle")}
        </button>

        <div className="mt-6 text-center text-xs text-slate-500">
          {t("auth.register.hasAccount")} <Link to="/login" className="font-semibold text-copper hover:underline">{t("auth.register.signIn")}</Link>
        </div>
      </div>
    </div>
  );
}
